// src/app/app.client.ts — Shell SPA (hash router) + AppHeader partajat.
// Un singur entry point HTML (app/index.html) montează acest shell, care comută
// între paginile aplicației fără reîncărcare. Header-ul (brand + selector de
// workspace + nav) e montat O DATĂ aici, deasupra rutelor — paginile nu mai au
// chrome propriu (excepție: landing-ul "/", care își păstrează nav-ul de prezentare).
// map / admin / documents sunt încărcate lazy (code-split) ca să țină landing-ul ușor.
import "../styles.css"
import van from "vanjs-core"
import pkg from "../../package.json"
import {
	current_path,
	get_hash_query_params,
	init_router,
	lazy,
	navigate,
	on_route_change,
	resolve_route,
	type Route,
} from "../lib/router.client"
import { set_workspace, workspace } from "../lib/workspace.client"
import { init_theme, ThemeToggle } from "../lib/theme.client"
import { install_client_error_capture } from "../lib/observe.client"
import { install_support_context } from "../lib/support_context.client"
import { HelpButton, mount_help_panel, SupportPage } from "../support/support.client"
import {
	init_pwa,
	install_available,
	prompt_install,
	PLAY_STORE_URL,
	is_android,
	is_standalone,
} from "../lib/pwa.client"
import { LandingPage } from "../home/home.client"
import { DocsPage, ensure_support_contact, SupportContactBlock } from "../docs/docs.client"
import { AuthPage, ForgotPasswordPage, ResetPasswordPage } from "../auth/auth.client"
import { AdminConsole } from "../admin/platform.client"
import {
	session,
	session_loaded,
	session_any_feature,
	load_session,
	logout,
	user_initials,
	user_label,
} from "../lib/session.client"

const t = van.tags

// Tema (dark mode) se aplică ÎNAINTE de montarea UI-ului — fără flash de temă greșită.
init_theme()
install_client_error_capture()
install_support_context()
init_pwa()

/**
 * Prompt „autentificare necesară" pentru zonele de BACK-OFFICE (IT-PORTAL-19: funcționalitățile
 * se populează conform drepturilor de acces; IT-PORTAL-37: acces prin portal de logare).
 */
function AccessRequired(): HTMLElement {
	const authed = session.val.authenticated
	const cta = authed
		? t.a(
				{
					href: "#/solicitari",
					class:
						"inline-block mt-6 px-5 py-2.5 rounded-lg bg-cyan-700 text-white font-medium hover:bg-cyan-800",
				},
				"Mergi la Solicitări",
			)
		: t.a(
				{
					href: `#/autentificare?return=${encodeURIComponent(location.hash.slice(1) || "/")}`,
					class:
						"inline-block mt-6 px-5 py-2.5 rounded-lg bg-cyan-700 text-white font-medium hover:bg-cyan-800",
				},
				"Autentificare",
			)
	return t.div(
		{
			class:
				"min-h-[calc(100vh-3rem)] flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4",
		},
		t.div(
			{ class: "max-w-md text-center" },
			t.div({ class: "text-4xl" }, "🔒"),
			t.h1(
				{ class: "mt-4 text-xl font-bold text-slate-800 dark:text-slate-200" },
				"Zonă de administrare",
			),
			t.p(
				{ class: "mt-2 text-slate-600 dark:text-slate-300" },
				authed
					? "Această secțiune e doar pentru utilizatorii primăriei. Contul tău nu are drepturi de administrare."
					: "Autentifică-te cu un cont de primărie pentru a accesa administrarea.",
			),
			cta,
		),
	)
}

/**
 * Gardă de rută: randează pagina DOAR când `allowed()` (staff / admin). Până se hidratează sesiunea
 * arată un spinner (evită flash-ul de „acces refuzat" la load rece); altfel un prompt de acces.
 * ⚠️ Pagina se construiește O SINGURĂ DATĂ per montare (`page`), nu la fiecare rulare a funcției
 * reactive: construită înăuntrul ei, fiecare stare citită la montare (store-ul paginii, workspace,
 * sesiune) devenea DEPENDENȚĂ a gărzii, iar orice schimbare a lor reconstruia toată pagina — dublu
 * montaj (cereri dublate) pe orice rută vizitată a doua oară (prima dată `lazy()` construiește pagina
 * într-o promisiune, în afara urmăririi — de aceea defectul apărea doar la revenire), iar pe
 * `#/documents?entity=<obiect șters>` o buclă infinită: montarea cerea contextul, 404-ul schimba
 * store-ul, garda remonta, montarea cerea din nou contextul (constatare 05.10, re-verificarea W4).
 * Rulările ulterioare întorc același nod — Van nu atinge DOM-ul când nodul e identic.
 */
function guard_route(allowed: () => boolean, component: () => HTMLElement): () => HTMLElement {
	return () => {
		let page: HTMLElement | null = null
		return t.div(() => {
			if (!session_loaded.val) {
				return t.div(
					{
						class:
							"flex justify-center items-center min-h-[200px] text-slate-400 dark:text-slate-500",
					},
					t.span({ class: "animate-pulse" }, "Se încarcă…"),
				)
			}
			if (!allowed()) {
				return AccessRequired()
			}
			page ??= component()
			return page
		})
	}
}

/** Randează pagina DOAR pentru staff (membru al unei primării sau super/support). */
function guard_staff(component: () => HTMLElement): () => HTMLElement {
	return guard_route(is_staff, component)
}

/** Ca guard_staff, dar cere ADMIN (owner/admin al unui org sau super) — pentru consola de administrare. */
function guard_admin(component: () => HTMLElement): () => HTMLElement {
	return guard_route(is_admin, component)
}

// ─────────────────────────────────────────────
// Rute
// ─────────────────────────────────────────────
const documents_module = lazy(() =>
	import("../documents/documents.client").then((m) => ({ default: m.DocumentsPage })),
)

const routes: Route[] = [
	{ path: "/", component: LandingPage },
	// Hub de hărți (staff): pagina din care alegi/comuți harta de lucru (înlocuiește selectorul din topbar).
	{
		path: "/harti",
		component: guard_staff(
			lazy(() => import("../home/hub.client").then((m) => ({ default: m.MapsHubPage }))),
		),
	},
	{
		path: "/map",
		component: lazy(() => import("../map/map.client").then((m) => ({ default: m.MapPage }))),
	},
	{
		path: "/admin",
		component: guard_staff(
			lazy(() => import("../admin/admin.client").then((m) => ({ default: m.AdminPage }))),
		),
	},
	// Documente / Registre / Șabloane = același modul lazy; pagina își alege
	// secțiunea din current_path.
	{
		path: "/solicitari",
		component: lazy(() =>
			import("../requests/requests.client").then((m) => ({ default: m.RequestsPage })),
		),
	},
	{ path: "/documents", component: guard_staff(documents_module) },
	{ path: "/registre", component: guard_staff(documents_module) },
	{ path: "/sabloane", component: guard_staff(documents_module) },
	{ path: "/rapoarte", component: guard_staff(documents_module) },
	{ path: "/docs", component: DocsPage },
	{ path: "/autentificare", component: AuthPage },
	{ path: "/inregistrare", component: AuthPage },
	// „Am uitat parola" (cerere) + setarea parolei noi din linkul primit pe email.
	{ path: "/recuperare-parola", component: ForgotPasswordPage },
	{ path: "/reset-parola", component: ResetPasswordPage },
	{ path: "/administrare", component: guard_admin(AdminConsole) },
	// Ajutor și suport (F14): pagina firelor către echipa de suport (deep-link din email: ?t=<id>).
	{ path: "/suport", component: guard_staff(SupportPage) },
	// „Profilul meu": orice user autentificat (inclusiv cetățean) → fără guard_staff.
	// Pagina își face singură promptul de autentificare pentru anonimi.
	{
		path: "/profil",
		component: lazy(() =>
			import("../profile/profile.client").then((m) => ({ default: m.ProfilePage })),
		),
	},
]

function NotFound() {
	return t.div(
		{
			class:
				"min-h-screen flex flex-col items-center justify-center text-center px-6 bg-white dark:bg-slate-900",
		},
		t.div({ class: "text-7xl font-extrabold text-slate-200" }, "404"),
		t.p({ class: "mt-4 text-lg text-slate-600 dark:text-slate-300" }, "Pagina nu există."),
		t.a(
			{
				href: "#/",
				class: "mt-6 px-5 py-2.5 rounded-lg bg-cyan-700 text-white font-medium hover:bg-cyan-800",
			},
			"Înapoi acasă",
		),
	)
}

// ─────────────────────────────────────────────
// AppHeader: brand + selector workspace + nav (h-12; vezi calc-ul din map.css)
// ─────────────────────────────────────────────

interface MapSummary {
	slug: string
	name: string
}

/**
 * Validează workspace-ul persistat contra listei curente de hărți accesibile: corectează numele sau
 * deselectează dacă harta nu mai e accesibilă (ștearsă/redenumită/fără drepturi după logout) — ca
 * paginile scope-uite să nu dea eroare. Alegerea/comutarea hărții se face din hub-ul #/harti.
 */
async function sync_workspace_validity() {
	const ws = workspace.val
	if (!ws.slug) {
		return
	}
	try {
		const res = await fetch("/api/maps", { credentials: "include" })
		const body = (await res.json()) as { ok: boolean; data?: MapSummary[] }
		if (!body.ok || !Array.isArray(body.data)) {
			return
		}
		const found = body.data.find((m) => m.slug === ws.slug)
		if (!found) {
			set_workspace("")
		} else if (found.name !== ws.name) {
			set_workspace(found.slug, found.name)
		}
	} catch {
		// fără listă — lăsăm workspace-ul cum e (paginile au propriile erori)
	}
}

/**
 * Meniul de cont din header: inițiale + NUMELE userului (nu emailul) + dropdown cu
 * „Profilul meu" / „Ieșire". Emailul rămâne vizibil, dar în antetul dropdown-ului.
 * Butonul „Administrare" rămâne separat, ca până acum.
 */
function UserMenu() {
	const open = van.state(false)
	const support_open = van.state(false)
	const close = () => {
		open.val = false
		support_open.val = false
	}

	// Hărțile accesibile pentru selectorul de workspace din meniu (încărcate la deschidere).
	const ws_maps = van.state<MapSummary[]>([])
	async function load_ws_maps() {
		try {
			const res = await fetch("/api/maps", { credentials: "include" })
			const body = (await res.json()) as { ok: boolean; data?: MapSummary[] }
			if (body.ok && Array.isArray(body.data)) {
				ws_maps.val = body.data
			}
		} catch {
			/* fără listă — selectorul rămâne cu opțiunea curentă */
		}
	}
	const ws_select = t.select({
		class:
			"w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 " +
			"px-2 py-1 text-xs text-slate-800 dark:text-slate-200 outline-none focus:border-cyan-500",
		onchange: (e: Event) => {
			const slug = (e.target as HTMLSelectElement).value
			const m = ws_maps.val.find((x) => x.slug === slug)
			set_workspace(slug, m?.name ?? slug)
			close()
		},
	}) as HTMLSelectElement
	van.derive(() => {
		const list = ws_maps.val
		const cur = workspace.val
		ws_select.replaceChildren(
			t.option({ value: "" }, "— fără hartă —"),
			...list.map((m) => t.option({ value: m.slug }, m.name || m.slug)),
			// harta curentă poate lipsi din listă (încă neîncărcată) — o păstrăm selectabilă
			...(cur.slug && !list.some((m) => m.slug === cur.slug)
				? [t.option({ value: cur.slug }, cur.name || cur.slug)]
				: []),
		)
		ws_select.value = cur.slug
	})

	// La logout nu lăsa dropdown-ul deschis — altfel reapare deschis la următorul login
	// (meniul e un singleton montat permanent, nu se re-creează).
	van.derive(() => {
		if (!session.val.authenticated) {
			close()
		}
	})

	// Închidere pe click în afară + Escape. Ascultătorii trăiesc cât ține pagina (header montat o dată).
	document.addEventListener("click", (e) => {
		if (open.val && !root.contains(e.target as Node)) {
			close()
		}
	})
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape") {
			close()
		}
	})

	const item =
		"block w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"

	const trigger = t.button(
		{
			class:
				"flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 " +
				"hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer flex-shrink-0",
			"aria-haspopup": "menu",
			"aria-expanded": () => String(open.val),
			onclick: (e: Event) => {
				e.stopPropagation()
				open.val = !open.val
				if (open.val && ws_maps.val.length === 0) {
					void load_ws_maps()
				}
			},
		},
		t.span(
			{
				class:
					"w-7 h-7 rounded-full bg-cyan-700 text-white flex items-center justify-center " +
					"text-[11px] font-bold flex-shrink-0",
			},
			() => user_initials(session.val.user),
		),
		// Nume user + (dedesubt) spațiul de lucru curent — VIZIBIL în bară, nu doar în dropdown.
		t.div(
			{ class: "hidden md:flex flex-col items-start leading-tight max-w-[12rem]" },
			t.span(
				{ class: "text-xs font-medium text-slate-700 dark:text-slate-200 truncate max-w-full" },
				() => user_label(session.val.user),
			),
			t.span(
				{
					class: () =>
						"text-[10px] truncate max-w-full " +
						(workspace.val.slug ? "text-cyan-700 dark:text-cyan-300" : "text-slate-400"),
				},
				() => (workspace.val.slug ? `🗺 ${workspace.val.name || workspace.val.slug}` : ""),
			),
		),
		t.span({ class: "text-slate-400 dark:text-slate-500 text-[10px]" }, "▼"),
	)

	const menu = t.div(
		{
			class: () =>
				(open.val ? "block" : "hidden") +
				" absolute right-0 top-full mt-1 w-56 rounded-lg border border-slate-200 dark:border-slate-700 " +
				"bg-white dark:bg-slate-900 shadow-lg py-1 z-50",
			role: "menu",
		},
		t.div(
			{ class: "px-3 py-2 border-b border-slate-100 dark:border-slate-800" },
			t.div({ class: "text-sm font-semibold text-slate-800 dark:text-slate-200 truncate" }, () =>
				user_label(session.val.user),
			),
			t.div(
				{ class: "text-xs text-slate-500 dark:text-slate-400 truncate" },
				() => session.val.user?.email ?? "",
			),
		),
		// Workspace-ul curent (F13): SELECTOR inline — schimbi harta direct din meniu, fără
		// navigare (set_workspace re-montează ruta curentă). Hub-ul rămâne pentru vederea completă.
		t.div(
			{ class: "px-3 py-2 border-b border-slate-100 dark:border-slate-800" },
			t.div(
				{ class: "text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1" },
				"Spațiu de lucru",
			),
			ws_select,
		),
		t.a({ href: "#/harti", class: item, role: "menuitem", onclick: close }, "Spații de lucru →"),
		t.a({ href: "#/profil", class: item, role: "menuitem", onclick: close }, "Profilul meu"),
		// Suport (decizia 1.8, PIN-008): canalul către furnizor — email/telefon din configurarea
		// instanței (SUPPORT_EMAIL/SUPPORT_PHONE prin /api/health), încărcate LA PRIMUL click, nu la
		// pornire; fără configurare, fallback-ul „contactați administratorul". Panoul e montat static
		// și comutat prin class (regula Van).
		t.button(
			{
				class: item,
				role: "menuitem",
				"aria-expanded": () => String(support_open.val),
				onclick: () => {
					support_open.val = !support_open.val
					if (support_open.val) {
						void ensure_support_contact()
					}
				},
			},
			"Suport",
		),
		t.div(
			{
				class: () =>
					(support_open.val ? "block" : "hidden") +
					" px-3 pb-2 border-b border-slate-100 dark:border-slate-800",
			},
			SupportContactBlock(true),
			t.a(
				{
					href: "#/docs?s=suport",
					class: "block mt-1.5 text-xs text-cyan-700 dark:text-cyan-300 hover:underline",
					role: "menuitem",
					onclick: close,
				},
				"Cum trimiți o sesizare →",
			),
		),
		// Legal (LEGAL_CHECKLIST.md D3): și funcționarii sunt persoane vizate (contul, istoricul atribuit).
		t.a(
			{
				href: "/termeni",
				target: "_blank",
				rel: "noopener",
				class: item,
				role: "menuitem",
				onclick: close,
			},
			"Termeni și condiții",
		),
		t.a(
			{
				href: "/confidentialitate",
				target: "_blank",
				rel: "noopener",
				class: item + " border-b border-slate-100 dark:border-slate-800",
				role: "menuitem",
				onclick: close,
			},
			"Confidențialitate",
		),
		// Instalare: pe ANDROID → Google Play (aplicația oficială „Urbanism by VEGO"); altfel →
		// promptul PWA, doar când browserul îl oferă (beforeinstallprompt). Ambele statice (Van).
		t.a(
			{
				href: PLAY_STORE_URL,
				target: "_blank",
				rel: "noopener",
				class: item + (is_android() && !is_standalone() ? "" : " hidden"),
				role: "menuitem",
				onclick: close,
			},
			"📱 Aplicația din Google Play",
		),
		t.button(
			{
				class: () => item + (!is_android() && install_available.val ? "" : " hidden"),
				role: "menuitem",
				onclick: () => {
					close()
					prompt_install()
				},
			},
			"⤓ Instalează aplicația",
		),
		t.button(
			{
				class: item + " text-slate-600 dark:text-slate-300",
				role: "menuitem",
				onclick: async () => {
					close()
					await logout()
					location.hash = "#/"
				},
			},
			"Ieșire",
		),
	)

	const root = t.div({ class: "relative flex-shrink-0" }, trigger, menu)
	return root
}

/**
 * Zona de cont din header: „Administrare" (dacă e admin) + meniul de user, altfel „Autentificare".
 *
 * ⚠️ TOT e montat de la început și se comută prin `class` — NIMIC nu se detașează niciodată.
 * Van șterge definitiv legăturile reactive ale nodurilor deconectate (`keepConnected`,
 * node_modules/vanjs-core/src/van.js:25, apelat din `updateDoms` la fiecare schimbare de stare).
 * Un `UserMenu` creat detașat (sau randat dintr-o ramură a unei funcții reactive, cât timp sesiunea
 * e încă anonimă) rămâne ÎNGHEȚAT pe valorile anonime — inițiale „?" și nume gol.
 * `display:none` NU face `isConnected=false`, deci ascunderea prin `class` păstrează legăturile vii.
 * Bonus: `UserMenu()` se instanțiază o singură dată → cei doi listeneri pe `document` nu se acumulează.
 */
const AUTH_BTN =
	"px-2.5 py-1.5 rounded-md text-xs font-semibold transition cursor-pointer flex-shrink-0"
/**
 * „Administrare” stă SEPARAT de zona de cont, ÎNAINTEA utilitarelor (Ajutor, temă): e navigație, nu utilitar.
 * Ordinea barei (feedback Ioana, 08.10): navigație → Administrare → ? Ajutor → temă → cont — utilitarele lângă
 * cont, unde le caută ochiul; pentru un funcționar fără drepturi de administrare bara rămâne Ajutor → temă → cont.
 */
function AdminLink() {
	return t.a(
		{
			href: "#/administrare",
			// `is_admin()` verifică deja `authenticated` (definit mai jos; declarație de funcție, hoistată).
			// max-md:hidden: pe mobil „Administrare" trăiește în panoul ☰ (bara rămâne curată).
			class: () =>
				is_admin()
					? "max-md:hidden " +
						AUTH_BTN +
						" border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
					: "hidden",
		},
		"Administrare",
	)
}
function AuthArea() {
	const btn = AUTH_BTN
	const authed = t.div(
		{ class: () => (session.val.authenticated ? "flex items-center gap-2" : "hidden") },
		UserMenu(),
	)
	const login_link = t.a(
		{
			href: "#/autentificare",
			class: () =>
				session.val.authenticated ? "hidden" : btn + " bg-cyan-700 text-white hover:bg-cyan-800",
		},
		"Autentificare",
	)
	return t.div({ class: "flex items-center flex-shrink-0" }, authed, login_link)
}

/**
 * Staff = user autentificat cu drepturi de BACK-OFFICE (membru al unei organizații sau rol de
 * platformă super/support). Cetățeanul pur (0 organizații) și anonimul NU sunt staff → nu văd
 * funcționalitățile de back-office (IT-PORTAL-19: funcționalitățile se populează conform drepturilor;
 * IT-BDGIS-34: portal intern pt. funcționari vs portal extern pt. cetățeni).
 */
function is_staff(): boolean {
	const s = session.val
	return (
		s.authenticated &&
		(s.memberships.length > 0 ||
			s.user?.platform_role === "super" ||
			s.user?.platform_role === "support")
	)
}

/**
 * Admin = poate accesa consola de administrare (#/administrare): super, sau owner/admin al unui org.
 * Din F14 și `support` (echipa de suport a platformei): consola îi arată DOAR inbox-ul de tichete.
 */
function is_admin(): boolean {
	const s = session.val
	if (!s.authenticated) {
		return false
	}
	if (s.user?.platform_role === "super" || s.user?.platform_role === "support") {
		return true
	}
	return s.memberships.some((m) => m.role === "owner" || m.role === "admin")
}

function AppHeader() {
	// [path, label, staff_only] — item-urile staff_only apar DOAR pentru staff (back-office).
	const nav_items: Array<[string, string, boolean]> = [
		["/harti", "Spații de lucru", true],
		["/map", "Hartă", false],
		["/solicitari", "Solicitări", false],
		["/documents", "Documente", true],
		["/registre", "Registre", true],
		["/sabloane", "Șabloane", true],
		["/rapoarte", "Rapoarte", true],
		["/admin", "Admin", true],
		["/docs", "Docs", false],
	]
	const link = (path: string, label: string) =>
		t.a(
			{
				href: `#${path}`,
				class: () =>
					current_path.val === path
						? "whitespace-nowrap px-2.5 py-1.5 rounded-md bg-cyan-700 text-white text-xs font-semibold transition"
						: "whitespace-nowrap px-2.5 py-1.5 rounded-md text-slate-600 dark:text-slate-300 text-xs font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition",
			},
			label,
		)
	// „Solicitări" dispare DOAR când toate org-urile userului au fluxurile de solicitări oprite
	// prin contract (PLAN_FEATURES_CONTRACT.md); cetățeanul/anonimul (fără org) îl păstrează —
	// directorul de primării e oricum filtrat pe server. Staff-ul de PLATFORMĂ (super/support)
	// îl vede mereu: administrează solicitările tuturor org-urilor (list_scope), indiferent de
	// contractele org-urilor în care e membru incidental.
	const requests_nav_visible = () => {
		const role = session.val.user?.platform_role
		if (role === "super" || role === "support") {
			return true
		}
		return (
			session_any_feature("pex_sesizari") ||
			session_any_feature("pex_consultare") ||
			session_any_feature("emitere_cu")
		)
	}
	// Nav-ul se re-randează la schimbarea sesiunii (login/logout) → item-urile de back-office
	// apar/dispar conform rolului (IT-PORTAL-19).
	// Desktop (md+): banda orizontală istorică. Mobil: banda e ASCUNSĂ — navigarea se face din
	// hamburger (☰) + panoul full-width de sub header (feedback testare 24.08: banda îngustă cu
	// scroll orizontal era greu de folosit; meniul de avatar ar fi avut descoperibilitate slabă).
	const nav_el = t.nav({ class: "hidden md:flex items-center gap-0.5 overflow-x-auto min-w-0" })

	// ── Panoul de navigare mobil (☰) — noduri STABILE comutate prin class (F8) ──
	const mobile_nav_open = van.state(false)
	const close_mobile_nav = () => (mobile_nav_open.val = false)
	const mobile_link = (path: string, label: string) =>
		t.a(
			{
				href: `#${path}`,
				onclick: close_mobile_nav,
				class: () =>
					"block w-full px-4 py-3 text-sm transition " +
					(current_path.val === path
						? "bg-cyan-50 dark:bg-cyan-950 text-cyan-700 dark:text-cyan-300 font-bold"
						: "text-slate-700 dark:text-slate-200 font-medium hover:bg-slate-50 dark:hover:bg-slate-800"),
			},
			label,
		)
	const mobile_nav_el = t.nav({ class: "flex flex-col py-1" })
	// Închidere la schimbarea rutei (deep-link/back) — nu doar la click pe item.
	van.derive(() => {
		void current_path.val
		close_mobile_nav()
	})
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape") {
			close_mobile_nav()
		}
	})

	van.derive(() => {
		const visible = nav_items.filter(([path, , staff]) => {
			if (staff && !is_staff()) {
				return false
			}
			if (path === "/solicitari") {
				return requests_nav_visible()
			}
			// Faza 2: Rapoartele sunt pe contract — nav-ul apare doar dacă VREUN org al userului
			// are modulul (super/support îl văd prin is_staff + ALL pe org necunoscut).
			if (path === "/rapoarte") {
				const role = session.val.user?.platform_role
				return role === "super" || role === "support" || session_any_feature("rapoarte")
			}
			return true
		})
		nav_el.replaceChildren(...visible.map(([p, l]) => link(p, l)))
		// Panoul mobil = aceleași destinații, vertical; „Administrare" (consola de platformă,
		// buton separat pe desktop) intră și el aici pe mobil.
		mobile_nav_el.replaceChildren(
			...visible.map(([p, l]) => mobile_link(p, l)),
			...(is_staff() ? [mobile_link("/suport", "Ajutor și suport")] : []),
			...(is_admin() ? [mobile_link("/administrare", "Administrare")] : []),
		)
	})

	const hamburger = t.button(
		{
			class:
				"md:hidden p-2 -m-1 text-xl leading-none text-slate-700 dark:text-slate-200 cursor-pointer",
			"aria-label": "Meniu de navigare",
			"aria-expanded": () => String(mobile_nav_open.val),
			onclick: (e: Event) => {
				e.stopPropagation()
				mobile_nav_open.val = !mobile_nav_open.val
			},
		},
		() => (mobile_nav_open.val ? "✕" : "☰"),
	)
	// Scrim-ul închide la tap în afară — ca la sidebar-ul hărții. ⚠️ Montat pe BODY, nu în
	// header: backdrop-blur-ul header-ului îl face containing block pentru `fixed`, iar scrim-ul
	// s-ar calcula în interiorul cutiei de 48px → înălțime zero, tap-în-afară mort (review 24.08).
	// z-30 < z-40 al header-ului: panoul și bara rămân deasupra scrim-ului.
	const mobile_nav_scrim = t.div({
		class: () =>
			"fixed inset-0 top-12 z-30 bg-black/30 md:hidden" + (mobile_nav_open.val ? "" : " hidden"),
		onclick: close_mobile_nav,
	})
	document.body.appendChild(mobile_nav_scrim)
	const mobile_nav_panel = t.div(
		{
			class: () =>
				"absolute top-full left-0 right-0 z-40 md:hidden bg-white dark:bg-slate-900 " +
				"border-b border-slate-200 dark:border-slate-700 shadow-lg max-h-[70dvh] overflow-y-auto" +
				(mobile_nav_open.val ? "" : " hidden"),
		},
		mobile_nav_el,
	)

	return t.header(
		{
			class:
				"sticky top-0 z-40 h-12 flex-shrink-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-700 " +
				"flex items-center justify-between gap-3 px-4",
		},
		mobile_nav_panel,
		t.div(
			{ class: "flex items-center gap-3 flex-shrink-0" },
			hamburger,
			t.a(
				{
					// Staff → hub-ul de hărți; anonim → landing-ul de marketing.
					href: () => (is_staff() ? "#/harti" : "#/"),
					class: "flex items-center gap-1.5 flex-shrink-0",
				},
				t.img({ src: "/public/logo-192.png", alt: "", class: "w-6 h-6" }),
				t.span(
					{ class: "text-cyan-700 dark:text-cyan-300 font-extrabold text-base tracking-tight" },
					"Urbanism",
				),
				t.span(
					{
						class:
							"hidden lg:inline-flex items-center whitespace-nowrap rounded-full bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300 " +
							"px-1.5 py-0.5 text-[9px] font-bold tracking-wide",
					},
					`v${pkg.version}`,
				),
			),
		),
		t.div(
			{ class: "flex-1 min-w-0 flex items-center justify-end gap-2" },
			nav_el,
			AdminLink(),
			// „Ajutor” (F14): panoul lateral cu ghidul + echipa de suport — doar conturile de lucru.
			HelpButton(),
			ThemeToggle(),
			AuthArea(),
		),
	)
}

// ─────────────────────────────────────────────
// Montare: header fix + swap de conținut în #app la fiecare schimbare de rută.
// Schimbarea workspace-ului re-montează ruta curentă (paginile citesc workspace la montare).
// ─────────────────────────────────────────────
const host = document.getElementById("app")!
const content_host = t.div()

// Deep link: ?map=slug din hash suprascrie workspace-ul persistat (numele se
// corectează după ce se încarcă lista de hărți).
const map_param = get_hash_query_params().get("map")
if (map_param && map_param !== workspace.val.slug) {
	set_workspace(map_param)
}

const header_wrap = t.div(
	{ style: () => (current_path.val === "/" ? "display:none" : "") },
	AppHeader(),
)
host.replaceChildren(header_wrap, content_host)
// Panoul „Ajutor” e montat O DATĂ, pe body, și comutat prin class (regula Van).
mount_help_panel()

init_router()
on_route_change((path) => {
	content_host.replaceChildren(resolve_route(routes, path, NotFound))
	window.scrollTo(0, 0)
})

// Lista de hărți s-a schimbat (import/creare/ștergere/reasignare) → re-validează workspace-ul
// (hub-ul #/harti își reîncarcă singur cardurile la același eveniment).
window.addEventListener("maps:changed", () => {
	sync_workspace_validity()
})

let mounted_workspace = workspace.val.slug
van.derive(() => {
	const slug = workspace.val.slug
	if (slug !== mounted_workspace) {
		mounted_workspace = slug
		content_host.replaceChildren(resolve_route(routes, current_path.val, NotFound))
	}
})

// Sesiunea (cookie) întâi; la fiecare schimbare de sesiune (login/logout) re-validăm workspace-ul.
load_session()
let last_auth: boolean | null = null
van.derive(() => {
	const authed = session.val.authenticated
	if (authed !== last_auth) {
		last_auth = authed
		sync_workspace_validity()
	}
})

// După hidratarea sesiunii, staff-ul aterizează pe HUB-ul de hărți (nu pe landing-ul de marketing):
// dacă e pe „/" și e staff, redirect la #/harti. Anonimii/cetățenii rămân pe landing.
van.derive(() => {
	if (session_loaded.val && is_staff() && current_path.val === "/") {
		navigate("/harti")
	}
})
